import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import axios from "axios";
import Store from "./store/Store";
import { runInAction, toJS } from "mobx";
import { getUrlParam } from "./helpers/url.helper";
import { ContentLayoutModule } from "./modules/ContentLayoutModule";
import "./layout.css";
import { ValidationSnackbar } from "./components/ValidationSnackbar";
import { FloatingGroupOrderCart } from "./modules/GroupRegistration/FloatingGroupOrderCart";
import { usePrevious } from "./helpers/usePrevious";
import { NotAuthorized } from "./components/NotAuthorized";
import SystemLabel from "../system/SystemLabel/SystemLabel";
import { PasswordDialog } from "./components/PasswordDialog";
import { PageClosed } from "./components/PageClosed";
import EventLabel from "./components/EventLabel";
import { useMediaQuery } from "@material-ui/core";
import json2mq from "json2mq";
import { LoadSpinner } from "./components/LoadSpinner";
import { addOrUpdateFont } from "./helpers/font.helper";
import { toJSON } from "cssjson";
import GridLayout from "react-grid-layout";
import AnimatedBackground from "./components/AnimatedBackground";
import { adobeTypekitParser } from "./helpers/adobeTypekit.helper";
import useLayoutRender from "./hooks/useLayoutRender";

const API_KEY_WEBFONTS = "AIzaSyB6aQW4uFg8OuFMHaxBmqDPCvzBGe5vuTg";

const Layout = observer((props) => {
  const store = scoped ? props : useContext(Store);
  const modulesRefs = useRef([]);
  const prevProps = usePrevious(props);
  const [showLoader, setShowLoader] = useState(true);
  const [clienFontsLoaded, setClientFontsLoaded] = useState(false);
  const [googleFontsLoaded, setGoogleFontsLoaded] = useState(false);
  const [fontAppended, setFontAppended] = useState(false);
  const [ordersLoaded, setOrdersLoaded] = useState(false);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [notAuthorized, setNotAuthorized] = useState(false);
  const [showPwdDialog, setShowPwdDialog] = useState(false);
  const [showPageClosed, setShowPageClosed] = useState(false);
  const [resized, setResized] = useState(+new Date());
  const bodyRef = useRef();
  const [contentStyles, setContentStyles] = useState("");
  const [fontStyles, setFontStyles] = useState("");
  const contentStyleRef = useRef();
  const fontStyleRef = useRef();

  const { scoped = false } = props;

  useEffect(() => {
    if (!scoped && prevProps && props.match && prevProps.match.params.id != props.match.params.id) init();
  });

  useEffect(() => {
    if (!scoped) init();
    let body = document.getElementsByTagName("body");
    for (const b of body) {
      b.classList.toggle("new-cms", true);
    }
    window.addEventListener("resize", windowOnResize);
    return () => {
      window.removeEventListener("resize", windowOnResize);
      let body = document.getElementsByTagName("body");
      for (const b of body) {
        b.classList.toggle("new-cms", false);
      }
    };
  }, []);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo(0, 0);
    }
  }, [store.current.activePage]);

  const windowOnResize = () => {
    setResized(+new Date());
  };

  useEffect(() => {
    if (clienFontsLoaded && googleFontsLoaded && layoutLoaded) {
      let fontstring = addOrUpdateFont(null, toJSON(store.current.template?.formattedCss ?? ""), store, "");
      fontstring += addOrUpdateFont(null, toJSON(store.contentLayout.css), store, "");
      if (fontstring != fontStyles) {
        setFontStyles(fontstring);
      }
      setFontAppended(true);
    }
  }, [clienFontsLoaded, googleFontsLoaded, layoutLoaded]);

  useEffect(() => {
    if (!fontStyleRef.current) {
      fontStyleRef.current = document.createElement("style");
      fontStyleRef.current.id = "THISONE";
      document.head.appendChild(fontStyleRef.current);
    }
    fontStyleRef.current.innerHTML = fontStyles;
  }, [fontStyles]);

  useEffect(() => {
    if (!contentStyleRef.current) {
      contentStyleRef.current = document.createElement("style");
      contentStyleRef.current.id = "THISTOO";
      document.head.appendChild(contentStyleRef.current);
    }
    contentStyleRef.current.innerHTML = contentStyles;
  }, [contentStyles]);

  useEffect(() => {
    if (ordersLoaded && layoutLoaded && !scoped) {
      let badgeNo = getUrlParam("badgeno");
      if (!badgeNo) badgeNo = getUrlParam("clientkey");
      if (store.contentLayout?.onlyAuthorizedUsers) {
        if (!badgeNo) {
          setNotAuthorized(true);
        }
      }
      if (store.contentLayout?.groupLeaderChangeLock) {
        var o = store.orders.find((x) => x.badgeNo == badgeNo);
        if (o && o.orderGroupId && o.orderGroupId !== o.orderId) {
          setNotAuthorized(true);
        }
      }
      if (store?.contentLayout?.type == "FORM") store.checkMainEventBookingStatus();
      setShowLoader(false);
    }
  }, [ordersLoaded, layoutLoaded]);

  useEffect(() => {
    if (store.contentLayout?.templateId && store.templates.length > 0) {
      runInAction(() => {
        store.current.template = store.templates.find((d) => d.templateId == store.contentLayout.templateId);
      });
    }
  }, [store.contentLayout, store.templates]);

  useEffect(() => {
    if (store.contentLayout?.activateGroupLeaderOnPage && store.contentLayout?.activateGroupLeaderOnPage == store.current?.activePage) {
      runInAction(() => {
        let leader = store.orders.find((x) => x.leader);
        if (leader) {
          store.setActiveOrder(store.orders.indexOf(leader));
        }
      });
    }
  }, [store.current?.activePage]);

  const fetchClientFonts = async () => {
    try {
      const { data } = await axios.get(`./api/files/fonts/${store.environment.eventId}`);

      if (data.files) {
        const parsedFiles = await Promise.all(
          data.files.map(async ({ type, name, url }) => {
            if (type.includes("css")) {
              const { importUrl, fonts } = await adobeTypekitParser(url);
              return fonts.map((font) => {
                const { name: _name, fontface } = font;
                return {
                  name: _name,
                  url,
                  fontface,
                  importUrl,
                  adobe: true,
                };
              });
            }

            return {
              name: name
                .substring(name.lastIndexOf("/") + 1, name.lastIndexOf(".") + 1)
                .toString()
                .replace(/[)(.1]/g, ""),
              url,
            };
          })
        );
        setClientFontsLoaded(true);
        runInAction(() => {
          store.uploadedFonts = parsedFiles.flat();
        });
      } else {
        setClientFontsLoaded(true);
      }
    } catch (err) {
      console.error(err);
      setClientFontsLoaded(true);
    }
  };

  // const fetchClientFonts = () => {
  //   axios
  //     .get(`./api/files/fonts/${store.environment.eventId}`)
  //     .then(({ data }) => {
  //       if (data.files) {
  //         runInAction(() => {
  //           store.uploadedFonts = data.files.map((f) => {
  //             const { name } = f;
  //             return {
  //               name: name
  //                 .substring(name.lastIndexOf("/") + 1, name.lastIndexOf(".") + 1)
  //                 .toString()
  //                 .replace(")", "")
  //                 .replace("(", "")
  //                 .replace(".", "")
  //                 .replace("1", "")
  //                 .replace("", ""),
  //               url: f.url,
  //             };
  //           });
  //           setClientFontsLoaded(true);
  //         });
  //       } else {
  //         setClientFontsLoaded(true);
  //       }
  //     })
  //     .catch((err) => {
  //       console.error(err);
  //       setClientFontsLoaded(true);
  //     });
  // };

  const fetchGoogleFonts = () => {
    axios
      .get(`https://www.googleapis.com/webfonts/v1/webfonts?sort=popularity&key=${API_KEY_WEBFONTS}`)
      .then(({ data }) => {
        runInAction(() => {
          store.googleFonts = data.items
            .splice(0, 200)
            .map((f) => {
              return f.family;
            })
            .sort(function (a, b) {
              if (a < b) {
                return -1;
              }
              if (a > b) {
                return 1;
              }
              return 0;
            });
          setGoogleFontsLoaded(true);
        });
      })
      .catch((err) => {
        setGoogleFontsLoaded(true);
        console.error(err);
      });
  };

  const setPageIcon = (url) => {
    let link = document.querySelector("link[rel=icon]");
    if (!link) {
      link = document.createElement("link");
      link.id = "favicon";
      link.rel = "icon";
      document.head.append(link);
    }
    link.href = url + "?=" + Math.random();
  };

  const init = () => {
    const { match } = props;
    let layoutCode = scoped ? props.layoutCode : match.params.id;
    let badgeNo = getUrlParam("badgeno");
    const xrayMode = getUrlParam("xray");

    if (xrayMode) {
      store.xrayMode = true;
    }

    if (!badgeNo) badgeNo = getUrlParam("clientkey");
    if (badgeNo) {
      getAttendee(badgeNo, layoutCode);
    } else {
      setOrdersLoaded(true);
    }
    //handle this different
    let item = localStorage.getItem("formstates");
    let prevStateNo = undefined;
    if (item) {
      let states = JSON.parse(item);
      let prevState = states.find((s) => s.formId === layoutCode && s.expires > Date.now());
      if (prevState) {
        prevStateNo = prevState.sqlStateNo;
      }
    }

    if (scoped) return;

    axios.get(`./api/cms/${layoutCode}/?newSqlStateNo=${prevStateNo === undefined}`).then(({ data }) => {
      runInAction(() => {
        store.environment.eventId = data.contentLayout.eventId;

        store.contentLayout = data.contentLayout;

        store.modules = data.contentModules;
        store.questions = data.questions;
        store.breakouts = data.breakouts;
        store.sqlStateNo = data.sqlStateNo;
        store.environment.currency = data.currency || "SEK";
        store.environment.eventCountry = data.eventCountry;
        store.contentHeaderLayout = data.contentHeaderLayout;
        store.contentFooterLayout = data.contentFooterLayout;
        store.mainEvent = data.mainEvent;

        if (store.contentLayout.conditions) {
          store.contentLayout.conditions = JSON.parse(store.contentLayout.conditions);
        }
        store.contentLayout.contentLayoutModules = store.contentLayout.contentLayoutModules.map((clm) => {
          clm.conditions = JSON.parse(clm.conditions);
          return clm;
        });

        store.current.hasContentView = store.contentLayout.contentLayoutModules.find((clm) => clm.contentModule.type == "CONTENT_VIEW");
        //Get the whole event or baseevent

        fetchGoogleFonts();
        fetchClientFonts();

        const displayEventSettings = {};
        data.displayEventSettings.map((de) => {
          displayEventSettings[de.name] = de.value;
        });
        store.displayEventSettings = displayEventSettings;
        const displayConfirmationSettings = {};
        data.displayConfirmationSettings.map((de) => {
          displayConfirmationSettings[de.name] = de.value;
        });
        store.displayConfirmationSettings = displayConfirmationSettings;
        store.fields = data.fields;
        store.current.template = data.templates.find((t) => t.templateId == data.contentLayout.templateId);
        store.defaultSelectedCountry = parseInt(data.defaultSelectedCountry);

        store.configureEnviroment(data.contentLayout, data);

        if (store.contentLayout.googleAnalytics) {
          appendGoogleAnalytics(store.contentLayout.googleAnalytics);
        }
        if (store.contentLayout.facebookPixel) {
          appendFacebookPixel(store.contentLayout.facebookPixel);
        }
        if (store.contentLayout.documentTitle) {
          document.title = store.contentLayout.documentTitle;
        } else if (store.mainEvent && store.mainEvent.name) {
          document.title = store.mainEvent.name;
        }
        if (store.contentLayout.faviconUrl) {
          setPageIcon(store.contentLayout.faviconUrl);
        }
        if ((data.contentLayout.openDateTime && +new Date(data.contentLayout.openDateTime) > Date.now()) || (data.contentLayout.closeDateTime && +new Date(data.contentLayout.closeDateTime) < Date.now())) {
          if (data.contentLayout?.redirectUrl) {
            window.location.href = data.contentLayout?.redirectUrl;
          } else setShowPageClosed(true);
        }

        if (data.contentLayout.password) {
          let pwdItem = localStorage.getItem(`pwd_${store.contentLayout.contentLayoutId}`);
          if (!pwdItem || atob(pwdItem) != data.contentLayout.password) {
            setShowPwdDialog(true);
          }
        }

        setContentStyles(`
        /*TEMPLATE*/
        ${store.current.template?.formattedCss ?? ""}
        /*LAYOUT*/
        ${store.contentLayout.css ?? ""}
        /*HEADER*/
        ${store?.contentHeaderLayout?.css ?? ""}
        /*FOOTER*/
        ${store?.contentFooterLayout?.css ?? ""}
        `);

        getCountries();
        getEventLanguages();
        if (bodyRef.current) {
          bodyRef.current.scroll({
            top: 0,
            left: 0,
            behavior: "smooth",
          });
        }
        if (data.contentLayout.type == "FORM") store.checkMainEventBookingStatus();
        else if (data.contentLayout.type == "SURVEY") checkIsSurveyAnswered(layoutCode, badgeNo);
        setLayoutLoaded(true);
      });
    });
  };

  const checkIsSurveyAnswered = async (layoutCode, badgeNo) => {
    if (layoutCode && badgeNo) {
      const { data } = await axios.get(`./api/attendee/survey/${layoutCode}/${badgeNo}`);
      const { isAnswered, redirectCode } = data;
      if (isAnswered && redirectCode) {
        window.location.href = `${window.location.origin}${!window.location.href.includes("localhost") ? "/cms" : ""}/web/${redirectCode}${window.location.search}`;
      }
    }
  };
  const appendGoogleAnalytics = (googleAnalyticsId) => {
    var script1 = document.createElement("script");
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId.replace(" ", "")}`;
    script1.async = true;
    document.head.appendChild(script1);

    var script2 = document.createElement("script");
    script2.text = `window.dataLayer = window.dataLayer || [];
                            function gtag(){dataLayer.push(arguments);}
                            gtag('js', new Date());
                            gtag('config', '${googleAnalyticsId.replace(" ", "")}');`;

    document.head.appendChild(script2);
  };

  const appendFacebookPixel = (facebookPixelId) => {
    var script2 = document.createElement("script");
    script2.text = `
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${facebookPixelId.replace(" ", "")}');
          fbq('track', 'PageView');
    `;
    document.head.appendChild(script2);
  };

  const getAttendee = (badgeNo, code) => {
    axios.get(`./api/attendee/${badgeNo}?layoutCode=${code}`).then(({ data }) => {
      if (Array.isArray(data)) {
        runInAction(() => {
          store.orders = data;
          setOrdersLoaded(true);
        });
      } else {
        setNotAuthorized(true);
      }
    });
  };

  const getCountries = () => {
    axios.get(`./api/country/`).then(({ data }) => {
      runInAction(() => {
        store.countries = data;
      });
    });
  };

  const getEventLanguages = () => {
    axios.get(`./api/language/${store.environment.eventId}`).then((res) => {
      const eventLanguages = res.data;
      runInAction(() => {
        store.event.languages = eventLanguages;
      });
    });
  };

  const isSmartphone = useMediaQuery(
    json2mq({
      maxWidth: 768,
    })
  );

  // const layout = useMemo(() => {
  //   if (scoped) return store.current.scopedLayout?.contentLayoutModules.filter((clm) => clm.pages === store.current.activePage) || [];
  //   return store.contentLayout?.contentLayoutModules.filter((clm) => clm.pages === store.current.activePage) || [];
  // }, [store.contentLayout?.contentLayoutModules, store.current.scopedLayout?.contentLayoutModules, store.current.activePage]);

  const layout = store.contentLayout?.contentLayoutModules.filter((clm) => clm.pages === store.current.activePage) ?? [];
  const headerLayout = store.contentHeaderLayout?.contentLayoutModules ?? [];
  const footerLayout = store.contentFooterLayout?.contentLayoutModules ?? [];

  const headerModules = useLayoutRender({
    layout: headerLayout,
    xrayMode: store.xrayMode,
    ...props,
    ...store,
    modulesRefs,
    isSmartphone,
    bodyWidth: bodyRef.current?.clientWidth || window.innerWidth,
  });

  const layoutModules = useLayoutRender({
    layout,
    xrayMode: store.xrayMode,
    ...props,
    ...store,
    modulesRefs,
    isSmartphone,
    bodyWidth: bodyRef.current?.clientWidth || window.innerWidth,
  });

  const footerModules = useLayoutRender({
    layout: footerLayout,
    xrayMode: store.xrayMode,
    ...props,
    ...store,
    modulesRefs,
    isSmartphone,
    bodyWidth: bodyRef.current?.clientWidth || window.innerWidth,
  });

  if (notAuthorized) {
    return (
      <NotAuthorized>
        <h1 style={{ fontFamily: "Be Vietnam" }}>
          <SystemLabel name="TheLinkIsNotValid" />
        </h1>
        <h4 style={{ fontFamily: "Be Vietnam", maxWidth: 500 }}>
          <SystemLabel name="TheLinkYouProvideIsNotValid" />
        </h4>
      </NotAuthorized>
    );
  }
  if (showLoader || !fontAppended || (showPageClosed && store?.contentLayout?.redirectUrl)) return <LoadSpinner />;

  return (
    <>
      {!showPageClosed && showPwdDialog ? <PasswordDialog onSuccess={() => setShowPwdDialog(false)} pwd={store.contentLayout.password} contentId={store.contentLayout.contentLayoutId} /> : null}
      {!showPageClosed && !showPwdDialog && store.contentLayout?.contentLayoutModules.find((cl) => cl.contentModule.type == "GROUP_REGISTRATION") ? <FloatingGroupOrderCart {...store} /> : null}

      <div ref={bodyRef} className="body" id="body" style={{ paddingBottom: isSmartphone ? "50px" : null }}>
        <AnimatedBackground body={bodyRef.current} />
        <ValidationSnackbar {...{ ...store, modulesRefs }} />
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {showPageClosed || store.mainEventFullbooked ? (
            <PageClosed openDateTime={store.contentLayout.openDateTime} closeDateTime={store.contentLayout.closeDateTime} onOpenPage={() => setShowPageClosed(false)}>
              <h1 style={{ fontFamily: "Be Vietnam" }}>
                <EventLabel name="PageClosedTitle" groupName="CMS.PageClosed" />
              </h1>
              <h4 style={{ fontFamily: "Be Vietnam", maxWidth: 500 }}>
                <EventLabel name="PageClosedBody" groupName="CMS.PageClosed" />
              </h4>
            </PageClosed>
          ) : !showPwdDialog ? (
            <>
              <div style={{ position: "relative" }}>
                <div className="cms-background-block" style={{ zIndex: 1 }}></div>
                <div style={{ position: "relative", zIndex: 2 }}>{headerModules}</div>
              </div>
              <div style={{ position: "relative" }}>
                <div className="cms-background-block" style={{ zIndex: 1 }}></div>
                <div style={{ position: "relative", zIndex: 2 }}>{layoutModules}</div>
              </div>
              <div style={{ position: "relative" }}>
                <div className="cms-background-block" style={{ zIndex: 1 }}></div>
                <div style={{ position: "relative", zIndex: 2 }}>{footerModules}</div>
              </div>

              {/* <div style={{ position: "relative" }}>
                <div className="cms-background-block"></div>
                <GridLayout
                  cols={12}
                  rowHeight={1}
                  onLayoutChange={onLayoutChange}
                  layout={headerLayout
                    ?.slice()
                    .sort((a, b) => a.posY - b.posY)
                    .map((clm) => {
                      return {
                        i: clm.contentLayoutModuleId.toString(),
                        x: isSmartphone ? 0 : clm.posX,
                        y: clm.posY - numberOfHiddenElements,
                        h: clm.renderHeight ?? clm.height,
                        w: isSmartphone ? 12 : clm.width,
                        minH: 0,
                      };
                    })}
                  width={bodyRef.current?.clientWidth || window.innerWidth}
                  isDraggable={false}
                  isResizable={false}
                  verticalCompact={true}
                  measureBeforeMount={true}
                  useCSSTransforms={false}
                  margin={[0, 0]}
                >
                  {headerLayout
                    ?.slice()
                    .sort((a, b) => a.posY - b.posY)
                    ?.map((clm, ii) => {
                      if (clm.renderHeight == 1) {
                        numberOfHiddenElements += 1;
                      }
                      return (
                        <div key={clm.contentLayoutModuleId.toString()}>
                          <ContentLayoutModule
                            {...{
                              ...clm,
                              ...store,
                              ...clm.contentModule,
                              isHeader: true,
                              scoped: props.scoped,
                              contentLayoutModule: clm,
                              modulesRefs,
                              isSmartphone,
                              bodyWidth: bodyRef.current?.clientWidth || window.innerWidth,
                              hiddenElements: numberOfHiddenElements,
                            }}
                          />
                        </div>
                      );
                    })}
                </GridLayout>
              </div>

              <div style={{ position: "relative" }}>
                <div className="cms-background-block"></div>
                <GridLayout
                  cols={12}
                  rowHeight={1}
                  onLayoutChange={onLayoutChange}
                  layout={layout
                    ?.filter((clm) => clm.pages == store.current.activePage)
                    .sort((a, b) => a.posY - b.posY)
                    .map((clm) => {
                      return {
                        i: clm.contentLayoutModuleId.toString(),
                        x: isSmartphone ? 0 : clm.posX,
                        y: clm.posY - numberOfHiddenElements,
                        h: clm.renderHeight ?? clm.height,
                        w: isSmartphone ? 12 : clm.width,
                        minH: 0,
                      };
                    })}
                  width={bodyRef.current?.clientWidth || window.innerWidth}
                  isDraggable={false}
                  isResizable={false}
                  verticalCompact={true}
                  measureBeforeMount={true}
                  useCSSTransforms={false}
                  margin={[0, 0]}
                >
                  {layout
                    ?.filter((clm) => clm.pages == store.current.activePage)
                    .sort((a, b) => a.posY - b.posY)
                    ?.map((clm, ii) => {
                      if (clm.renderHeight == 1) {
                        numberOfHiddenElements += 1;
                      }
                      return (
                        <div key={clm.contentLayoutModuleId.toString()}>
                          <ContentLayoutModule
                            {...{
                              ...clm,
                              ...store,
                              ...clm.contentModule,
                              scoped: props.scoped,
                              contentLayoutModule: clm,
                              modulesRefs,
                              isSmartphone,
                              bodyWidth: bodyRef.current?.clientWidth || window.innerWidth,
                              hiddenElements: numberOfHiddenElements,
                            }}
                          />
                        </div>
                      );
                    })}
                </GridLayout>
              </div>
              <div style={{ position: "relative" }}>
                <div className="cms-background-block"></div>
                <GridLayout
                  cols={12}
                  rowHeight={1}
                  onLayoutChange={onLayoutChange}
                  layout={footerLayout
                    ?.slice()
                    .sort((a, b) => a.posY - b.posY)
                    .map((clm) => {
                      return {
                        i: clm.contentLayoutModuleId.toString(),
                        x: isSmartphone ? 0 : clm.posX,
                        y: clm.posY - numberOfHiddenElements,
                        h: clm.renderHeight ?? clm.height,
                        w: isSmartphone ? 12 : clm.width,
                        minH: 0,
                      };
                    })}
                  width={bodyRef.current?.clientWidth || window.innerWidth}
                  isDraggable={false}
                  isResizable={false}
                  verticalCompact={true}
                  measureBeforeMount={true}
                  useCSSTransforms={false}
                  margin={[0, 0]}
                >
                  {footerLayout
                    ?.slice()
                    .sort((a, b) => a.posY - b.posY)
                    ?.map((clm, ii) => {
                      if (clm.renderHeight == 1) {
                        numberOfHiddenElements += 1;
                      }
                      return (
                        <div key={clm.contentLayoutModuleId.toString()}>
                          <ContentLayoutModule
                            {...{
                              ...clm,
                              ...store,
                              ...clm.contentModule,
                              isFooter: true,
                              scoped: props.scoped,
                              contentLayoutModule: clm,
                              modulesRefs,
                              isSmartphone,
                              bodyWidth: bodyRef.current?.clientWidth || window.innerWidth,
                              hiddenElements: numberOfHiddenElements,
                            }}
                          />
                        </div>
                      );
                    })}
                </GridLayout>
              </div> */}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
});

export default Layout;
